import React, { useEffect } from 'react';
import { CallbackFn, useFormInput, UseFormInput, ValidatorType } from '../../../../hooks/useFormInput';
import { HttpTransaction, MsgTransaction, Service, Tag } from '../../../../types/rest/assetCatalogTypes';
import SaveTxnModal from './Modal/SaveTxnModal';
import { Col, Form, Row } from 'react-bootstrap';
import i18n from '../../../../i18n';
import BzFormControlWithHook from '../../../../components/forms/BzFormControlWithHook';
import Button from 'react-bootstrap/Button';
import styles from '../../../MockServices/Tabs/MockServices/MockServices.module.css';
import HelpTooltip from '../../../../components/overlays/helpTooltip/HelpTooltip';
import ServicePicker from '../../../../components/ui/servicePicker/ServicePicker';
import TagSelector from '../../../../components/selectors/TagSelector';
import { EditorTabType, tagSelectHandler, TransactionEditorProps, TransactionField } from './TransactionEditor';
import { dateAndAuthorFormat } from '../../../../utils/dateUtil';
import MixpanelService from '../../../../utils/mixpanelService';
import { CHANGE_TRANSACTION_SERVICE } from '../../../../utils/mixpanelEvents';

const TransactionCommonHeader = (props: {
  saveTransaction: (selectedTab?: EditorTabType) => void;
  setIsSaveModalShown: (value: boolean) => void;
  saveModalShown: boolean;
  editorProps: TransactionEditorProps;
  serviceId: number;
  setServiceId: (value: number) => void;
  setServiceName: (value: string) => void;
  setDirty: (dirty: boolean) => void;
  validating: boolean;
  transactionState: HttpTransaction | MsgTransaction;
  tagSelectHandler: (tags: Tag[]) => void;
  onChange: (event) => void;
  updateNameInput: (nameInput: UseFormInput) => void;
  updateTransactionState: (field: TransactionField, value: any) => void;
}) => {
  const nameInput = useFormInput(
    props.transactionState.name,
    (fn: CallbackFn) => {
      props.updateTransactionState('name', fn.value);
    },
    {
      type: ValidatorType.REQUIRED,
      invalidMessage: i18n.t('transactions:editor.txnNameRequired'),
    },
    {
      type: ValidatorType.MAX_LENGTH,
      value: 250,
      invalidMessage: i18n.t('transactions:editor.txnNameMaxLength'),
    },
    {
      type: ValidatorType.INVALID_LEADING_SPACES,
      invalidMessage: i18n.t('transactions:editor.txnNameIsInvalid'),
    },
    {
      type: ValidatorType.INVALID_CHARACTERS,
      invalidMessage: i18n.t('transactions:editor.txnNameIsInvalid'),
    },
  );

  useEffect(() => {
    props.updateNameInput(nameInput);
  }, [nameInput.value, nameInput.dirty, nameInput.valid]);

  /**
   * Message showing this is sample transaction already created by the wizard.
   */
  const sampleTransactionMsg = () => (
    <div className="alert alert-info pb-0">
      <i className="icon icon-information alert-icon"></i>
      <p className="alert-message">{i18n.t('wizard:details.behavior.sampleMsg')}</p>
    </div>
  );

  const createdMessage = dateAndAuthorFormat(props.transactionState.created, props.transactionState.createdBy);
  const updatedMessage = dateAndAuthorFormat(props.transactionState.updated, props.transactionState.updatedBy);

  const onTxnSaveFromModal = async () => {
    props.setIsSaveModalShown(false);
    await props.saveTransaction('processingActions');
  };

  const handleServiceChange = (vs: Service | null) => {
    props.setServiceId(vs && vs.id != null ? vs.id : 0);
    props.setServiceName(vs ? vs.name : '');
    props.setDirty(true);
    MixpanelService.track(CHANGE_TRANSACTION_SERVICE());
  };

  return (
    <>
      {props.saveModalShown && (
        <SaveTxnModal onSave={onTxnSaveFromModal} onClose={() => props.setIsSaveModalShown(false)} />
      )}
      {props.editorProps.isWizardModal === true && props.editorProps.transaction.id === 1 && sampleTransactionMsg}
      <Row>
        <Col md={4}>
          <Form.Group controlId="qa-input-transaction-name-te">
            <div className="form-group-heading">
              <Form.Label>{i18n.t('transactions:table.txnName')}</Form.Label>
            </div>
            <div className="position-relative">
              <BzFormControlWithHook
                autoComplete="off"
                disabled={props.editorProps.isReadOnly}
                useFormInputHook={nameInput}
                placeholder={i18n.t('transactions:table.txnName')}
                required={true}
              />
            </div>
          </Form.Group>
        </Col>

        {props.editorProps.isEmbedded !== true && (
          <>
            {props.editorProps.isWizardModal !== true && (
              <Col md={4}>
                <Form.Label>
                  {props.editorProps.isArchivedView
                    ? i18n.t('transactions:table.deleted')
                    : i18n.t('transactions:table.created')}
                </Form.Label>
                <div id={'transactionCreatedDate'} className="mt-1 text-truncate">
                  <span title={createdMessage}> {createdMessage}</span>
                </div>
              </Col>
            )}
            {props.editorProps.isWizardModal !== true ? (
              <Col md={3}>
                <Form.Label>{i18n.t('transactions:table.updated')}</Form.Label>
                <div id={'transactionUpdatedDate'} className="mt-1 text-truncate">
                  <span title={updatedMessage}> {updatedMessage}</span>
                </div>
              </Col>
            ) : (
              <Col md={7} />
            )}

            <Col md={1} className="text-md-right">
              {props.editorProps.isWizardModal !== true && (
                <Button
                  disabled={props.editorProps.isReadOnly}
                  onClick={() => props.saveTransaction()}
                  id="qa-btn-save-transaction"
                  title={i18n.t('transactions:editor.saveTxn')}
                >
                  {i18n.t('common:actions.save')}
                </Button>
              )}
            </Col>
          </>
        )}
      </Row>
      {props.editorProps.isWizardModal !== true && (
        <Row>
          <Col md={4}>
            <Form.Group>
              <div className="form-group-heading justify-content-start">
                <Form.Label className="d-inline" htmlFor={'service-picker-' + props.serviceId}>
                  Service
                </Form.Label>
                <span title={i18n.t('common:showHelp')} className={styles.questionNearService}>
                  <HelpTooltip id={'Transactions_advanced_help'} />
                </span>
              </div>
              <ServicePicker
                selectionDisabled={props.editorProps.isReadOnly || props.editorProps.isEmbedded}
                isCreateEnabled={true}
                id={'service-picker-' + props.serviceId}
                selectHandler={handleServiceChange}
                isSelectAll={false}
                preSelected={props.serviceId}
              />

              {(!props.serviceId || props.serviceId === 0) && props.validating && (
                <div className="invalid-feedback">{i18n.t('transactions:editor.serviceRequired')}</div>
              )}
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label htmlFor="react-select-multiple">{i18n.t('transactions:table.tags')}</Form.Label>
              <TagSelector
                isReadOnly={props.editorProps.isReadOnly}
                preSelected={props.transactionState.tags}
                selectHandler={props.tagSelectHandler}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group controlId="qa-input-description-te">
              <Form.Label>{i18n.t('transactions:editor.description')}</Form.Label>
              <Form.Control
                autoComplete="off"
                disabled={props.editorProps.isReadOnly}
                as="input"
                onChange={props.onChange}
                defaultValue={props.transactionState.description ? props.transactionState.description : ''}
                placeholder={i18n.t('transactions:editor.enterDescription')}
              />
            </Form.Group>
          </Col>
        </Row>
      )}
    </>
  );
};

export default TransactionCommonHeader;
